import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Alert, Button, Card, Input, InputNumber, Select, Space, Spin, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { renderExperimentsApi } from '../services/catalogServiceAPI/renderExperimentsProxy';
import { DEFAULT_CRITERIA } from './shared';

const { Title, Text } = Typography;
const { TextArea } = Input;

const TIME_OPTIONS = [{ value: '', label: 'Any time of day' }, { value: 'day', label: 'Day' }, { value: 'twilight', label: 'Twilight' }, { value: 'night', label: 'Night' }];
const ENV_OPTIONS = [
    { value: '', label: 'Any environment' },
    ...['countryside', 'urban', 'beach', 'lakeside', 'mountains', 'forest', 'desert', 'tropical', 'snow', 'vineyard']
        .map((value) => ({ value, label: value[0].toUpperCase() + value.slice(1) })),
];
const KIND_OPTIONS = [{ value: 'generate', label: 'Generate' }, { value: 'existing', label: 'Existing production output' }];
const SOURCE_MODE_OPTIONS = [{ value: 'latest', label: 'Latest renderings' }, { value: 'ids', label: 'Specific renderings' }];
const MAX_SOURCES = 200;

const parseRenderingIds = (text) => [...new Set(text.split(/[\s,]+/).map((id) => id.trim()).filter(Boolean))];
const listIds = (ids) => ids.slice(0, 5).join(', ') + (ids.length > 5 ? ` and ${ids.length - 5} more` : '');

let seq = 0;
const withKey = (item) => ({ key: `new-${++seq}`, ...item });
const newCriterion = () => withKey({ label: '', min: 1, max: 10 });

const newArm = (defaults, providerId, label) => {
    const provider = defaults.providers.find((p) => p.id === providerId) || defaults.providers[0];
    return {
        key: `new-${++seq}`,
        label: label || provider?.label || 'Arm',
        kind: 'generate',
        provider: provider?.id,
        model: provider?.defaultModel || '',
        promptTemplate: defaults.defaultPromptTemplate,
    };
};

const defaultArms = (defaults) => [
    newArm(defaults, 'openai', 'ChatGPT'),
    newArm(defaults, 'xai', 'Grok'),
];

export function RenderExperimentForm() {
    const history = useHistory();
    const location = useLocation();
    const { id } = useParams();
    const preselectedIds = location.state?.renderingIds;
    const [defaults, setDefaults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [criteria, setCriteria] = useState(() => DEFAULT_CRITERIA.map(withKey));
    const [sourceCount, setSourceCount] = useState(50);
    const [timeOfDay, setTimeOfDay] = useState('');
    const [environment, setEnvironment] = useState('');
    const [arms, setArms] = useState([]);
    const [sourceMode, setSourceMode] = useState(preselectedIds?.length ? 'ids' : 'latest');
    const [idsText, setIdsText] = useState(preselectedIds?.length ? preselectedIds.join('\n') : '');
    const [eligible, setEligible] = useState(null);
    const [eligibleError, setEligibleError] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const d = await renderExperimentsApi.defaults();
                setDefaults(d);
                if (id) {
                    const exp = await renderExperimentsApi.get(id);
                    if (exp.status !== 'draft') {
                        history.replace(`/experiments/${id}`);
                        return;
                    }
                    setName(exp.name);
                    setDescription(exp.description || '');
                    setCriteria((exp.criteria || DEFAULT_CRITERIA).map(withKey));
                    setSourceCount(exp.sourceCount);
                    setTimeOfDay(exp.sourceFilter?.timeOfDay || '');
                    setEnvironment(exp.sourceFilter?.environment || '');
                    if (exp.sourceFilter?.renderingIds?.length) {
                        setSourceMode('ids');
                        setIdsText(exp.sourceFilter.renderingIds.join('\n'));
                    }
                    setArms(exp.arms.map((a) => ({ key: a.id, ...a, promptTemplate: a.promptTemplate || d.defaultPromptTemplate })));
                } else {
                    setArms(defaultArms(d));
                }
            } catch (e) {
                message.error(e.message);
            } finally {
                setLoading(false);
            }
        })();
    }, [id, history]);

    const renderingIds = useMemo(() => parseRenderingIds(idsText), [idsText]);

    useEffect(() => {
        const request = sourceMode === 'ids'
            ? (renderingIds.length ? { count: renderingIds.length, filter: { renderingIds } } : null)
            : (sourceCount ? { count: sourceCount, filter: { timeOfDay, environment } } : null);
        if (!request) {
            setEligible(null);
            setEligibleError(false);
            return undefined;
        }
        let cancelled = false;
        // Debounced: the ids textarea fires on every keystroke.
        const timer = setTimeout(() => {
            renderExperimentsApi.eligibleSources(request.count, request.filter)
                .then((r) => { if (!cancelled) { setEligible(r); setEligibleError(false); } })
                .catch(() => { if (!cancelled) { setEligible(null); setEligibleError(true); } });
        }, 400);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [sourceMode, sourceCount, timeOfDay, environment, renderingIds]);

    const sourceIssues = useMemo(() => {
        const issues = [];
        if (eligibleError) issues.push("Couldn't check which renderings are eligible — the API request failed.");
        if (sourceMode === 'ids' && renderingIds.length > MAX_SOURCES) {
            issues.push(`At most ${MAX_SOURCES} renderings can be used; ${renderingIds.length} were given.`);
        }
        if (!eligible) return issues;
        if (sourceMode === 'ids') {
            if (eligible.missingIds?.length) {
                issues.push(`${eligible.missingIds.length} id${eligible.missingIds.length === 1 ? " doesn't" : "s don't"} match any rendering: ${listIds(eligible.missingIds)}`);
            }
            if (eligible.ineligibleIds?.length) {
                issues.push(`${eligible.ineligibleIds.length} rendering${eligible.ineligibleIds.length === 1 ? '' : 's'} can't be used as a source (videos, failed renders, or renders made before source screenshots were stored): ${listIds(eligible.ineligibleIds)}`);
            }
        } else if (eligible.count < sourceCount) {
            issues.push(`Only ${eligible.count} of the latest ${sourceCount} renderings are eligible (renders made before source screenshots were stored can't be used).`);
        }
        return issues;
    }, [eligible, eligibleError, sourceMode, renderingIds, sourceCount]);


    const updateCriterion = (key, patch) => setCriteria((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch } : c)));
    const removeCriterion = (key) => setCriteria((prev) => prev.filter((c) => c.key !== key));

    const updateArm = (key, patch) => setArms((prev) => prev.map((a) => (a.key === key ? { ...a, ...patch } : a)));
    const removeArm = (key) => setArms((prev) => prev.filter((a) => a.key !== key));
    const onProviderChange = (arm, providerId) => {
        const provider = defaults.providers.find((p) => p.id === providerId);
        updateArm(arm.key, { provider: providerId, model: provider?.defaultModel || arm.model });
    };

    const save = async () => {
        setSaving(true);
        try {
            const input = {
                name,
                description,
                criteria: criteria.map(({ key, ...c }) => c),
                sourceCount: sourceMode === 'ids' ? renderingIds.length : sourceCount,
                sourceFilter: sourceMode === 'ids'
                    ? { renderingIds }
                    : { timeOfDay: timeOfDay || undefined, environment: environment || undefined },
                arms: arms.map(({ key, ...a }) => (id && !key.startsWith('new-') ? { id: key, ...a } : a)),
            };
            const saved = id ? await renderExperimentsApi.update(id, input) : await renderExperimentsApi.create(input);
            message.success('Experiment saved');
            history.push(`/experiments/${saved.id}`);
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading || !defaults) return <div style={{ padding: 24 }}><Spin /></div>;

    const providerOptions = defaults.providers.map((p) => ({ value: p.id, label: p.label }));
    const criteriaValid = criteria.length >= 1 && criteria.every((c) => c.label.trim() && Number.isInteger(c.min) && Number.isInteger(c.max) && c.min < c.max);
    const sourcesValid = sourceMode === 'ids' ? renderingIds.length >= 1 && renderingIds.length <= MAX_SOURCES : sourceCount >= 1;
    const canSave = name.trim() && criteriaValid && sourcesValid && arms.length >= 2 && arms.every((a) => a.label.trim());

    return (
        <div style={{ padding: 24, maxWidth: 1100 }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Title level={3} style={{ margin: 0 }}>{id ? 'Edit experiment' : 'New experiment'}</Title>

                <Card title="Setup" size="small">
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Input placeholder="Name (e.g. Grok vs ChatGPT, Sept 2026)" value={name} onChange={(e) => setName(e.target.value)} />
                        <TextArea placeholder="Notes (optional)" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
                        <Space wrap>
                            <span>Sources</span>
                            <Select style={{ width: 190 }} value={sourceMode} options={SOURCE_MODE_OPTIONS} onChange={setSourceMode} />
                            {sourceMode === 'latest' && (
                                <>
                                    <span>Latest</span>
                                    <InputNumber min={1} max={MAX_SOURCES} value={sourceCount} onChange={(v) => setSourceCount(v || 1)} />
                                    <span>renderings</span>
                                    <Select style={{ width: 170 }} value={timeOfDay} options={TIME_OPTIONS} onChange={setTimeOfDay} />
                                    <Select style={{ width: 170 }} value={environment} options={ENV_OPTIONS} onChange={setEnvironment} />
                                </>
                            )}
                            {sourceMode === 'ids' && (
                                <Text type="secondary">{renderingIds.length} rendering{renderingIds.length === 1 ? '' : 's'}</Text>
                            )}
                        </Space>
                        {sourceMode === 'ids' && (
                            <TextArea
                                rows={6}
                                placeholder="Rendering ids — one per line or comma separated"
                                value={idsText}
                                onChange={(e) => setIdsText(e.target.value)}
                                style={{ fontFamily: 'monospace', fontSize: 12 }}
                            />
                        )}
                        {sourceIssues.length > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                message={sourceIssues.length === 1 ? sourceIssues[0] : (
                                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                                        {sourceIssues.map((issue) => <li key={issue}>{issue}</li>)}
                                    </ul>
                                )}
                            />
                        )}
                    </Space>
                </Card>

                <Card
                    title="What are we testing?"
                    size="small"
                    extra={<Button icon={<PlusOutlined />} size="small" onClick={() => setCriteria((p) => [...p, newCriterion()])} disabled={criteria.length >= 10}>Add parameter</Button>}
                >
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Text type="secondary">Raters score every candidate on each parameter, blind to which arm produced it. Rendering duration and failure rate are always measured.</Text>
                        {criteria.map((c) => (
                            <Space key={c.key} wrap>
                                <Input style={{ width: 260 }} placeholder="Parameter (e.g. Accuracy)" value={c.label} onChange={(e) => updateCriterion(c.key, { label: e.target.value })} />
                                <span>scale</span>
                                <InputNumber style={{ width: 70 }} min={0} max={99} value={c.min} onChange={(v) => updateCriterion(c.key, { min: v })} />
                                <span>–</span>
                                <InputNumber style={{ width: 70 }} min={1} max={100} value={c.max} onChange={(v) => updateCriterion(c.key, { max: v })} />
                                <Button danger type="text" size="small" icon={<DeleteOutlined />} onClick={() => removeCriterion(c.key)} disabled={criteria.length <= 1} />
                            </Space>
                        ))}
                    </Space>
                </Card>

                <Card
                    title="Arms"
                    size="small"
                    extra={<Button icon={<PlusOutlined />} size="small" onClick={() => setArms((p) => [...p, newArm(defaults, 'openai', `Arm ${p.length + 1}`)])}>Add arm</Button>}
                >
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {arms.map((arm, index) => (
                            <Card
                                key={arm.key}
                                size="small"
                                type="inner"
                                title={`Arm ${index + 1}`}
                                extra={<Button danger type="text" size="small" icon={<DeleteOutlined />} onClick={() => removeArm(arm.key)} disabled={arms.length <= 2} />}
                            >
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <Space wrap>
                                        <Input style={{ width: 200 }} placeholder="Label" value={arm.label} onChange={(e) => updateArm(arm.key, { label: e.target.value })} />
                                        <Select style={{ width: 240 }} value={arm.kind} options={KIND_OPTIONS} onChange={(kind) => updateArm(arm.key, { kind })} />
                                        {arm.kind === 'generate' && (
                                            <>
                                                <Select style={{ width: 180 }} value={arm.provider} options={providerOptions} onChange={(p) => onProviderChange(arm, p)} />
                                                <Input style={{ width: 240 }} placeholder="Model" value={arm.model} onChange={(e) => updateArm(arm.key, { model: e.target.value })} />
                                            </>
                                        )}
                                    </Space>
                                    {arm.kind === 'generate' && (
                                        <>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                                <Text type="secondary">Prompt template — placeholders: [ENVIRONMENT] [TIME] [WINDOWS] [MAIN_LIGHT] [LIGHT_SOURCES] [MIRRORS] [SOFT_ITEMS] [ROOM_CONTENTS]</Text>
                                                <Select
                                                    size="small"
                                                    style={{ width: 320 }}
                                                    placeholder="Load a preset…"
                                                    value={(defaults.promptPresets || []).find((p) => p.template === arm.promptTemplate)?.id ?? null}
                                                    options={(defaults.promptPresets || []).map((p) => ({ value: p.id, label: p.label }))}
                                                    onChange={(id) => updateArm(arm.key, { promptTemplate: defaults.promptPresets.find((p) => p.id === id).template })}
                                                />
                                            </div>
                                            <TextArea rows={8} value={arm.promptTemplate} onChange={(e) => updateArm(arm.key, { promptTemplate: e.target.value })} style={{ fontFamily: 'monospace', fontSize: 12 }} />
                                        </>
                                    )}
                                </Space>
                            </Card>
                        ))}
                    </Space>
                </Card>

                <Space>
                    <Button type="primary" loading={saving} disabled={!canSave} onClick={save}>Save draft</Button>
                    <Button onClick={() => history.push(id ? `/experiments/${id}` : '/experiments')}>Cancel</Button>
                </Space>
            </Space>
        </div>
    );
}
